import {Drawer} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Center} from 'src/design/atoms/Center';
import {Divider} from 'src/design/atoms/Divider';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {SimpleGrid} from 'src/design/atoms/SimpleGrid';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {IconCalendarDue, IconRepeat} from '@tabler/icons-react';
import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import type {InspectionSimpleResponse} from 'src/api/ProactApiClient';
import {Button} from 'src/design/atoms/Button';
import {NavAnchor} from 'src/design/atoms/NavAnchor';
import {Pill} from 'src/design/atoms/Pill';
import {ActiveInactivePills} from 'src/design/atoms/pillPresets';
import {ContentTitle} from 'src/design/atoms/Typography';
import {ViewField} from 'src/design/atoms/ViewField';
import {NoItemsPanel} from 'src/design/molecules/NoItemsPanel';
import {StatTile} from 'src/design/molecules/StatTile';
import {useGetInspectionListV2Query} from 'src/redux/queries/inspection';
import {useGetInspectionScheduleQuery} from 'src/redux/queries/inspectionSchedule';
import {useInspectionPermissions} from 'src/shared/auth/permissions/useInspectionPermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {InspectionStateEnumPill} from 'src/shared/constants/PillConstants';
import {formatUtcShortDate} from 'src/shared/utils/formatters';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {
  cadenceLabel,
  formatSiteLocalDate,
  isScheduleOverdue,
  OVERDUE_PILL,
} from './inspectionScheduleHelpers';
import {ScheduleHealthPills} from './ScheduleHealthPills';

const PAGE_SIZE = 10;

export interface InspectionScheduleDrawerProps {
  opened: boolean;
  onClose: () => void;
  scheduleId?: number;
}

const GeneratedInspectionRow: React.FC<{
  inspection: InspectionSimpleResponse;
}> = ({inspection}) => (
  <Group justify="space-between" wrap="nowrap" py="xs">
    <Group gap="xs" wrap="nowrap">
      <Pill {...InspectionStateEnumPill(inspection.state)} />
      <div>
        <Text size="sm">Due {formatUtcShortDate(inspection.dueAt)}</Text>
        <Text size="xs" c="dimmed">
          {inspection.assigneeName ?? 'Unassigned'}
          {inspection.completedAt
            ? ` · Completed ${formatUtcShortDate(inspection.completedAt)}`
            : ''}
        </Text>
      </div>
    </Group>
    <NavAnchor
      text="View"
      component={Link}
      to={PathUtility.InspectionsView(inspection.id)}
    />
  </Group>
);

const GeneratedInspections: React.FC<{scheduleId: number; opened: boolean}> = ({
  scheduleId,
  opened,
}) => {
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  useEffect(() => {
    setPageSize(PAGE_SIZE);
  }, [scheduleId, opened]);

  const {data, isLoading, isError, error} = useGetInspectionListV2Query(
    {
      start: 0,
      length: pageSize,
      filters: [],
      filterFns: [],
      sorting: [{id: 'dueAt', desc: true}],
      manageView: true,
      assignedToAnyone: true,
      inspectionScheduleId: scheduleId,
    },
    {skip: !opened}
  );

  const rows = data?.data ?? [];
  const totalCount = data?.totalRecords ?? 0;

  return (
    <div>
      <Group justify="space-between" wrap="nowrap" mb="xs">
        <Text size="sm" fw={600}>
          Generated inspections
        </Text>
        <NavAnchor
          text="Open in Manage"
          component={Link}
          to={`${PathUtility.Inspections('manage')}?scheduleId=${scheduleId}`}
        />
      </Group>

      {isLoading ? (
        <Center py="md">
          <Loader size="sm" />
        </Center>
      ) : isError ? (
        <Alert color="red" variant="light">
          {parseErrorMessage(error)}
        </Alert>
      ) : rows.length === 0 ? (
        <NoItemsPanel noItemsText="This schedule has not generated any inspections yet." />
      ) : (
        <Stack gap={0}>
          {rows.map((inspection, index) => (
            <div key={inspection.id}>
              {index > 0 && <Divider />}
              <GeneratedInspectionRow inspection={inspection} />
            </div>
          ))}
          {rows.length < totalCount && (
            <Button
              variant="subtle"
              mt="xs"
              onClick={() => setPageSize(size => size + PAGE_SIZE)}>
              Load more
            </Button>
          )}
        </Stack>
      )}
    </div>
  );
};

/**
 * Read-only drawer for one inspection recurrence schedule.
 * @registry Drawer showing an inspection schedule summary plus the inspections it generated.
 */
export const InspectionScheduleDrawer: React.FC<
  InspectionScheduleDrawerProps
> = ({opened, onClose, scheduleId}) => {
  const perms = useInspectionPermissions();
  const canManage = perms.manage || perms.manageAll;

  const {
    data: schedule,
    isLoading,
    isError,
    error,
  } = useGetInspectionScheduleQuery(scheduleId as number, {
    skip: !opened || scheduleId === undefined,
  });

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      title="Recurrence Schedule">
      {isLoading ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : isError ? (
        <Alert color="red" variant="light" title="Could not load the schedule">
          {parseErrorMessage(error)}
        </Alert>
      ) : !schedule ? (
        <NoItemsPanel noItemsText="Schedule not found." />
      ) : (
        <Stack>
          <div>
            <ContentTitle text={schedule.siteName} />
            <Text size="sm" c="dimmed">
              {schedule.inspectionTypeName}
              {schedule.clientName ? ` · ${schedule.clientName}` : ''}
            </Text>
          </div>

          <Group gap={4} wrap="wrap">
            {schedule.latestInspectionState && (
              <Pill
                {...InspectionStateEnumPill(schedule.latestInspectionState)}
              />
            )}
            {isScheduleOverdue(schedule) && <Pill {...OVERDUE_PILL} />}
            <Pill {...ActiveInactivePills(schedule.active)} />
            <ScheduleHealthPills schedule={schedule} />
          </Group>

          <NavAnchor
            text="View site"
            component={Link}
            to={PathUtility.SitesView(schedule.siteId)}
          />

          <Divider />

          <SimpleGrid cols={2}>
            <StatTile
              title="Next Due"
              value={formatSiteLocalDate(
                schedule.nextDueAt,
                schedule.siteTimeZone
              )}
              icon={<IconCalendarDue size={20} />}
            />
            <StatTile
              title="Generated"
              value={schedule.generatedCount}
              icon={<IconRepeat size={20} />}
            />
          </SimpleGrid>

          <SimpleGrid cols={2}>
            <ViewField label="Cadence" value={cadenceLabel(schedule)} />
            <ViewField
              label="Lead time"
              value={`${schedule.leadTimeDays} days`}
            />
            <ViewField
              label="Anchor"
              value={formatSiteLocalDate(
                schedule.anchorDate,
                schedule.siteTimeZone
              )}
            />
            <ViewField
              label="Generates on"
              value={formatSiteLocalDate(
                schedule.nextGenerationAt,
                schedule.siteTimeZone
              )}
            />
          </SimpleGrid>

          {canManage ? (
            <>
              <Divider />
              <GeneratedInspections scheduleId={schedule.id} opened={opened} />
            </>
          ) : (
            <Text size="sm" c="dimmed">
              Inspections generated by this schedule are visible to users who
              manage inspections.
            </Text>
          )}
        </Stack>
      )}
    </Drawer>
  );
};
